/**
 * Read-time upcasting (decision 0036), shared pieces: ids, actors, references and placements. Every transform maps a
 * decoded v1 value (`V1.X`, the frozen schemas) to the current type. Branded ids differ between the two (`TaskId` ≠
 * `ItemId`), so the compiler finds every field that needs a mapping; each union is switched exhaustively.
 */
import type { Actor, EntityRef, NodeRef } from "../common"
import type { Mind } from "../domain/org"
import type { ItemId } from "../ids"
import type * as V1 from "../legacy/v1/index"
import type { Affinity, Placement, Unmet } from "../placement"

/** A v1 task, initiative or intake id as an item id: ids are never rewritten (`tsk_`, `ini_`, `itk_` stay). */
export const itemId = (id: string): ItemId => id as ItemId

/**
 * The retired mind shapes (the `hosted` placeholder, a platform mind's named `credential`) read as the nearest current
 * mind, as `Mind` itself decodes them: a platform mind on Anthropic, and the workspace key.
 */
export const fromV1Mind = (m: V1.Mind): Mind => {
  switch (m._tag) {
    case "runner":
      return m
    case "hosted":
      return {
        _tag: "platform",
        provider: "anthropic",
        model: m.model,
        credential: { _tag: "workspace_default" },
      }
    case "platform":
      return { ...m, credential: { _tag: "workspace_default" } }
  }
}

export const fromV1Actor = (a: V1.Actor): Actor => {
  switch (a._tag) {
    case "worker":
      return {
        _tag: "worker",
        runId: a.runId,
        itemId: itemId(a.taskId),
        agentId: a.agentId,
        onBehalfOf: a.onBehalfOf,
      }
    case "user":
    case "agent":
    case "runner":
    case "system":
      return a
    default:
      return a satisfies never
  }
}

/** createdBy / updatedBy of a v1 record. */
export const fromV1Meta = <R extends { readonly createdBy: V1.Actor; readonly updatedBy: V1.Actor }>(
  r: R,
): Omit<R, "createdBy" | "updatedBy"> & { readonly createdBy: Actor; readonly updatedBy: Actor } => ({
  ...r,
  createdBy: fromV1Actor(r.createdBy),
  updatedBy: fromV1Actor(r.updatedBy),
})

export const fromV1EntityRef = (r: V1.EntityRef): EntityRef => {
  switch (r._tag) {
    case "task":
    case "initiative":
    case "intake":
      return { _tag: "item", id: itemId(r.id) }
    case "goal":
    case "kpi":
    case "area":
    case "agent":
    case "edge":
    case "proposal":
    case "memory":
    case "artifact":
    case "comment":
    case "run":
    case "policy":
      return r
    default:
      return r satisfies never
  }
}

export const fromV1NodeRef = (r: V1.NodeRef): NodeRef => {
  switch (r._tag) {
    case "task":
    case "initiative":
      return { _tag: "item", id: itemId(r.id) }
    case "goal":
    case "kpi":
    case "area":
    case "agent":
      return r
    default:
      return r satisfies never
  }
}

type V1WorkRef = typeof V1.WorkRef.Type
type V1RankedRef = typeof V1.RankedRef.Type

export const fromV1WorkRef = (r: V1WorkRef): { readonly _tag: "item"; readonly id: ItemId } => {
  switch (r._tag) {
    case "task":
    case "initiative":
      return { _tag: "item", id: itemId(r.id) }
    default:
      return r satisfies never
  }
}

export const fromV1RankedRef = (
  r: V1RankedRef,
): { readonly _tag: "goal"; readonly id: V1.GoalId } | { readonly _tag: "item"; readonly id: ItemId } => {
  switch (r._tag) {
    case "goal":
      return r
    case "task":
    case "initiative":
      return { _tag: "item", id: itemId(r.id) }
    default:
      return r satisfies never
  }
}

type V1Affinity = typeof V1.Affinity.Type
type V1Placement = typeof V1.Placement.Type

export const fromV1Affinity = (a: V1Affinity): typeof Affinity.Type => {
  const { worktreeOf, ...rest } = a
  return worktreeOf === undefined ? rest : { ...rest, worktreeOf: itemId(worktreeOf) }
}

export const fromV1Placement = (p: V1Placement): Placement => ({
  ...p,
  affinity: p.affinity === null ? null : fromV1Affinity(p.affinity),
})

export const fromV1Unmet = (u: V1.Unmet): Unmet => {
  switch (u._tag) {
    case "affinity_unmet":
      return { _tag: "affinity_unmet", worktreeOf: u.worktreeOf === null ? null : itemId(u.worktreeOf) }
    case "no_runner_online":
    case "runner_offline":
    case "runner_paused":
    case "owner_lock":
    case "ceiling_refuses":
    case "missing_tool":
    case "missing_repo":
    case "missing_agent":
    case "profile_not_ready":
    case "profile_out_of_scope":
    case "profile_at_capacity":
    case "rate_limited":
    case "at_capacity":
    case "exclusive_held":
    case "runner_outdated":
      return u
    default:
      return u satisfies never
  }
}
